<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import type { IReservationShiftDto } from '@/api/services/reservations.service';
  import V2ReservationShiftModal from '@/components/modals/reservations/V2ReservationShiftModal.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import { useReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import { useModal } from '@/composition/use-modal';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { reservationDepositScopePatch } from '@/domain/reservations/reservation-deposit-scope';
  import {
    GUIDE_DURATION_CHIPS,
    SEEDED_SHIFT_NAME,
    chipValues,
    proposedReservationShifts,
  } from '@/domain/reservations/reservation-guide';
  import type { IShiftProposal } from '@/domain/reservations/reservation-guide';
  import type { IGuestPreviewInput } from '@/domain/reservations/reservation-guest-preview';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IReservationSettingsConfiguration,
    IReservationShift,
  } from '@/interfaces/IReservationSettings';
  import V2ReservationsGuideShiftRow from './V2ReservationsGuideShiftRow.vue';
  import { guideDurationLabel } from './reservation-guide-copy';

  /** Step 1 · Turnos: stored shifts, the two local proposals (D14) and the duration disclosure (D11). */
  type TPreviewShift = IGuestPreviewInput['shifts'][number];
  const context = useReservationGuide();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const { width } = useStableWindowSize();
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });

  const configuration = computed(() => context.configuration.value);
  const settings = computed(() => configuration.value?.settings ?? null);
  const busy = computed(() => context.busy.value);
  const storedShifts = computed(() =>
    [...(configuration.value?.shifts ?? [])].sort(
      (a, b) => a.sort - b.sort || a.id - b.id
    )
  );
  const proposals = ref<IShiftProposal[]>(
    storedShifts.value.length ? [] : proposedReservationShifts()
  );
  // Both proposals return whenever the branch is left without shifts.
  watch(
    () => storedShifts.value.length,
    (length, previous) => {
      if (previous > 0 && length === 0)
        proposals.value = proposedReservationShifts();
    }
  );
  const renaming = ref<number | null>(null);
  const stayOpen = ref(false);
  /** What the tapped chip will store; shown until the response lands or the write fails. */
  const optimisticDuration = ref<number | null>(null);

  const helpText = computed(() =>
    tr(
      storedShifts.value.length > 0 && proposals.value.length === 0
        ? 'reservations.guide.hours.seededHelp'
        : 'reservations.guide.hours.proposedHelp'
    )
  );
  const ready = computed(
    () => storedShifts.value.length + proposals.value.length > 0
  );
  const duration = computed(
    () => optimisticDuration.value ?? settings.value?.defaultDurationMin ?? 0
  );
  const durationRules = computed(() => settings.value?.durationRules ?? []);
  const durationChips = computed(() =>
    chipValues(GUIDE_DURATION_CHIPS, duration.value)
  );
  const durationLabel = computed(() => guideDurationLabel(duration.value, tr));
  /** With per-party rules the stay is a range: the shortest to the longest rule. */
  const stayRange = computed(() => {
    const minutes = [
      duration.value,
      ...durationRules.value.map((rule) => rule.minutes),
    ];
    return {
      min: guideDurationLabel(Math.min(...minutes), tr),
      max: guideDurationLabel(Math.max(...minutes), tr),
    };
  });
  const previewShifts = computed<TPreviewShift[]>(() =>
    proposals.value.map((proposal) => ({
      name: proposalName(proposal),
      days: proposal.days,
      from: proposal.from,
      to: proposal.to,
      arrivalTimes: null,
      slotIntervalMin: proposal.slotIntervalMin,
    }))
  );

  function current(configurationAtStart: IReservationSettingsConfiguration) {
    const scope = context.scope();
    return (
      alive &&
      !!scope &&
      scope.restaurantId === configurationAtStart.settings.restaurantId &&
      scope.branchId === configurationAtStart.settings.branchId
    );
  }
  function proposalName(proposal: IShiftProposal): string {
    return tr(
      proposal.key === 'lunch'
        ? 'reservations.guide.hours.renameLunch'
        : 'reservations.guide.hours.renameDinner'
    );
  }
  function isRenameable(shift: IReservationShift): boolean {
    return SEEDED_SHIFT_NAME.test(shift.name);
  }
  function removeProposal(key: IShiftProposal['key']) {
    proposals.value = proposals.value.filter(
      (proposal) => proposal.key !== key
    );
  }
  function chipLabel(minutes: number): string {
    return guideDurationLabel(minutes, tr);
  }

  /** Same modal and options as the Ajustes shifts card; a proposal prefills it instead of editing. */
  function openModal(target: {
    shift?: IReservationShift;
    proposal?: IShiftProposal;
  }) {
    const cfg = configuration.value;
    if (!cfg || busy.value) return;
    modals.show(
      V2ReservationShiftModal,
      {
        configuration: cfg,
        settings: cfg.settings,
        settingsDirty: false,
        shift: target.shift,
        prefill: target.proposal
          ? {
              name: proposalName(target.proposal),
              days: target.proposal.days,
              from: target.proposal.from,
              to: target.proposal.to,
              slotIntervalMin: target.proposal.slotIntervalMin,
            }
          : undefined,
      },
      {
        name: 'v2-reservation-shift',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 520,
        maxWidth: Math.round(width.value * 0.95),
        clickToClose: false,
      },
      {
        saved: () => {
          if (!current(cfg)) return;
          if (target.proposal) removeProposal(target.proposal.key);
          void context.reload();
        },
      }
    );
  }
  async function removeShift(shift: IReservationShift) {
    const cfg = configuration.value;
    if (!cfg || busy.value) return;
    const accepted = await modals.confirm({
      title: tr('reservations.shifts.deleteTitle'),
      message: tr('reservations.shifts.deleteMessage'),
      acceptText: tr('delete'),
      acceptColor: 'error',
    });
    if (!accepted || !current(cfg) || busy.value) return;
    const result = await context.run(async () => {
      await ReservationsService.deleteShift(shift.id);
      await context.reload();
      return true;
    }, tr('reservations.shifts.error'));
    if (result && current(cfg))
      ui.showToast(AlertType.Success, tr('reservations.shifts.deleted'));
  }
  /** The full DTO the modal would send for this shift, with only the name replaced. */
  async function rename(shift: IReservationShift, name: string) {
    const cfg = configuration.value;
    if (!cfg || busy.value || renaming.value !== null) return;
    const dto: IReservationShiftDto = {
      restaurantId: shift.restaurantId,
      branchId: shift.branchId,
      name,
      days: shift.days,
      from: shift.from.slice(0, 5),
      to: shift.to.slice(0, 5),
      maxCovers: shift.maxCovers,
      arrivalTimes: shift.arrivalTimes,
      slotIntervalMin: shift.slotIntervalMin,
      sort: shift.sort,
      ...reservationDepositScopePatch(shift.depositRequired, shift),
    };
    renaming.value = shift.id;
    await context.run(async () => {
      await ReservationsService.updateShift(shift.id, dto);
      await context.reload();
    }, tr('reservations.shifts.error'));
    if (alive) renaming.value = null;
  }
  async function pickDuration(minutes: number) {
    if (busy.value) return;
    optimisticDuration.value = minutes;
    await context.savePatch({ defaultDurationMin: minutes, durationRules: [] });
    // Success: the merged settings carry the value; failure: back to the stored one.
    if (alive) optimisticDuration.value = null;
  }
  /** Creates the proposals still listed, one by one, then moves on. */
  async function continueStep() {
    const cfg = configuration.value;
    if (!cfg || !ready.value || busy.value) return;
    const { restaurantId, branchId } = cfg.settings;
    const result = await context.run(async () => {
      let created = 0;
      let failure: unknown = null;
      try {
        for (const proposal of [...proposals.value]) {
          await ReservationsService.createShift({
            restaurantId,
            branchId,
            name: proposalName(proposal),
            days: proposal.days,
            from: proposal.from,
            to: proposal.to,
            maxCovers: null,
            arrivalTimes: null,
            slotIntervalMin: proposal.slotIntervalMin,
            sort: proposal.key === 'lunch' ? 0 : 1,
          });
          created++;
          if (!current(cfg)) return false;
          removeProposal(proposal.key);
        }
      } catch (cause) {
        failure = cause;
      }
      // A failed proposal stays listed; what was created must show at once.
      const reloaded = created > 0 ? await context.reload() : true;
      if (failure !== null) throw failure;
      // A failed reload keeps the step: its toast already explains.
      return reloaded;
    }, tr('reservations.shifts.error'));
    if (result && current(cfg)) context.next();
  }

  defineExpose({ ready, continue: continueStep, previewShifts });
</script>

<template>
  <div class="reservations-guide-step-hours">
    <h2 class="step-title">{{ $t('reservations.guide.hours.title') }}</h2>
    <p class="step-help">{{ helpText }}</p>
    <div class="shift-list">
      <V2ReservationsGuideShiftRow
        v-for="shift in storedShifts"
        :key="`shift-${shift.id}`"
        class="shift-item"
        :name="shift.name"
        :days="shift.days"
        :from="shift.from"
        :to="shift.to"
        :slot-interval-min="shift.slotIntervalMin"
        :arrival-times="shift.arrivalTimes"
        :renameable="isRenameable(shift)"
        :renaming="renaming === shift.id"
        :disabled="busy"
        @change="openModal({ shift })"
        @remove="removeShift(shift)"
        @rename="rename(shift, $event)"
      />
      <V2ReservationsGuideShiftRow
        v-for="proposal in proposals"
        :key="`proposal-${proposal.key}`"
        class="shift-item"
        :name="proposalName(proposal)"
        :days="proposal.days"
        :from="proposal.from"
        :to="proposal.to"
        :slot-interval-min="proposal.slotIntervalMin"
        :disabled="busy"
        @change="openModal({ proposal })"
        @remove="removeProposal(proposal.key)"
      />
      <p v-if="!ready" class="shift-empty">
        {{ $t('reservations.guide.hours.empty') }}
      </p>
    </div>
    <PosUiButton
      class="add-button"
      color="neutral"
      variant="outline"
      icon="plus"
      :disabled="busy"
      @click="openModal({})"
      >{{ $t('reservations.guide.hours.add') }}</PosUiButton
    >
    <div class="stay-row">
      <i18n
        v-if="durationRules.length"
        path="reservations.guide.hours.stayByParty"
        tag="p"
        class="stay-text"
      >
        <template #min
          ><strong>{{ stayRange.min }}</strong></template
        >
        <template #max
          ><strong>{{ stayRange.max }}</strong></template
        >
      </i18n>
      <i18n
        v-else
        path="reservations.guide.hours.stay"
        tag="p"
        class="stay-text"
      >
        <template #duration
          ><strong>{{ durationLabel }}</strong></template
        >
      </i18n>
      <PosUiButton
        color="neutral"
        variant="ghost"
        :aria-expanded="stayOpen ? 'true' : 'false'"
        aria-controls="reservations-guide-stay-editor"
        @click="stayOpen = !stayOpen"
        >{{
          $t(stayOpen ? 'reservations.guide.done' : 'reservations.guide.change')
        }}</PosUiButton
      >
    </div>
    <div
      v-show="stayOpen"
      id="reservations-guide-stay-editor"
      class="stay-editor"
    >
      <div
        class="chip-row"
        role="group"
        :aria-label="
          $t('reservations.guide.hours.stay', { duration: durationLabel })
        "
      >
        <PosUiChip
          v-for="minutes in durationChips"
          :key="minutes"
          :active="!durationRules.length && minutes === duration"
          :disabled="busy"
          @click="pickDuration(minutes)"
          >{{ chipLabel(minutes) }}</PosUiChip
        >
      </div>
      <p class="fine-print">{{ $t('reservations.guide.hours.stayHint') }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-step-hours {
    @apply tw-flex tw-flex-col tw-gap-4;
    p {
      @apply tw-m-0;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-neutral-900;
    }
    .step-help {
      @apply tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }
    .shift-list {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200;
      .shift-item + .shift-item {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }
    .shift-empty {
      @apply tw-px-4 tw-py-4 tw-text-sm tw-text-amber-700;
    }
    .add-button {
      @apply tw-self-start;
    }
    .stay-row {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-pt-4;
    }
    .stay-text {
      @apply tw-text-sm tw-text-neutral-700;
      strong {
        @apply tw-font-semibold tw-text-neutral-900;
      }
    }
    .stay-editor {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-bg-canvas tw-p-4;
    }
    .chip-row {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .fine-print {
      @apply tw-text-xs tw-text-neutral-500;
    }
  }
</style>
